import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCheckCircle,
  faCheckSquare,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import handleSectionAlignment from "../Utilities/handleSectionAlignment";
import ButtonLink from "./ButtonLink";
import {
  AlignmentType,
  BulletPointType,
  ContentModel,
  ImageWidthType,
} from "../Models/PageCreatorPage";
import ContainerWrapper from "./ContainerWrapper";
import LeadGenImageVideo from "./LeadGenImageVideo";
import { getSanitizedHtml } from "../Utilities/htmlInputUtility";

const LeadGenContent = ({
  alignmentType,
  backgroundColor,
  backgroundImageUrl,
  bodyText,
  bodyTextColor,
  bulletPointColor,
  bulletPointStyle,
  buttonBackgroundColor,
  buttonText,
  buttonTextColor,
  buttonUrl,
  buttonUrlType,
  caption,
  captionTextColor,
  containerColor,
  containerOutlineColor,
  hasBottomPadding,
  hasButton,
  hasCaption,
  hasContainer,
  hasDropShadow,
  hasImageOrVideo,
  hasListItems,
  hasOutline,
  hasTopPadding,
  header,
  headerTextColor,
  imageAlignment,
  imageAltText,
  imageUrl,
  imageWidth,
  listItems,
  useImageForBackground,
  video,
}: ContentModel): JSX.Element => {
  const sortedListItems = listItems?.sort(
    (a, b) => a.listPosition - b.listPosition,
  );

  const isLeftAligned =
    alignmentType === AlignmentType.Left || hasImageOrVideo || hasListItems;

  const handleBulletPointStyle = (type: BulletPointType | undefined) => {
    switch (type) {
      case BulletPointType.RoundCheck:
        return faCheckCircle;
      case BulletPointType.SquareCheck:
        return faCheckSquare;
      case BulletPointType.DefaultCheck:
        return faCheck;
      case BulletPointType.RoundX:
        return faTimesCircle;
      default:
        return faCheck;
    }
  };

  let imageVideoWidthClass = "lg:w-1/2";
  let textContentWidthClass = "lg:w-1/2";

  switch (imageWidth) {
    case ImageWidthType.OneThird:
      imageVideoWidthClass = "lg:w-1/3";
      textContentWidthClass = "lg:w-2/3";
      break;
    case ImageWidthType.TwoThirds:
      imageVideoWidthClass = "lg:w-2/3";
      textContentWidthClass = "lg:w-1/3";
      break;
    default:
      break;
  }

  return (
    <div
      className={`px-6 md:px-12 relative
        ${hasBottomPadding ? "pb-12 md:pb-24" : "pb-0"}
        ${hasTopPadding ? "pt-12 md:pt-24" : "pt-0"}
      `}
      id="content-wrapper"
      style={{
        background: useImageForBackground
          ? `url("${backgroundImageUrl}") no-repeat center center`
          : backgroundColor,
        backgroundSize: "cover",
      }}
    >
      <ContainerWrapper
        additionalClasses="max-w-6xl mx-auto"
        containerColor={containerColor}
        containerOutlineColor={containerOutlineColor}
        hasContainer={hasContainer}
        hasDropShadow={hasDropShadow}
        hasOutline={hasOutline}
        hasRoundedBorder
      >
        <LeadGenImageVideo
          caption={caption}
          captionTextColor={captionTextColor}
          hasCaption={hasCaption}
          hasImageOrVideo={hasImageOrVideo}
          imageAlignment={imageAlignment}
          imageAltText={imageAltText}
          imageUrl={imageUrl}
          imageVideoWidthClass={imageVideoWidthClass}
          video={video}
          layoutStyles={`${handleSectionAlignment(
            hasImageOrVideo,
            alignmentType,
          )}
            ${hasContainer ? "lg:p-16 md:p-12 p-6" : "lg:p-0 md:p-0 p-0"}
            ${hasImageOrVideo ? "items-center" : "items-start"}
            flex
            flex-col
            gap-6
            lg:flex-row
            lg:gap-12
            mx-auto
          `}
          content={
            <div
              id="text-content"
              data-testid="text-content"
              className={`
                flex
                flex-col
                ${isLeftAligned ? "items-start" : "items-center"}
                ${textContentWidthClass}
                w-full
              `}
            >
              {!!header && (
                <h2
                  className={`
                  break-words
                  ${isLeftAligned ? "text-left" : "text-center"}
                  font-bold
                  mb-0
                  max-w-2xl
                  md:text-4xl
                  lg:text-5xl
                  text-3xl
                  w-full
                  [text-wrap:balance]
                `}
                  style={{ color: headerTextColor, textShadow: "none" }}
                >
                  {header}
                </h2>
              )}
              {!!bodyText && (
                <p
                  className={`
                  [text-wrap:balance]
                  ${isLeftAligned ? "text-left" : "text-center"}
                  break-words
                  leading-tight
                  lt:mt-2
                  m-0
                  max-w-2xl
                  mt-1
                  text-base
                `}
                  style={{ color: bodyTextColor }}
                >
                  {getSanitizedHtml(bodyText)}
                </p>
              )}
              {hasListItems && (
                <ul
                  className={`
                  mb-0
                  ml-0
                  mt-4
                `}
                >
                  {sortedListItems.map((item) => (
                    <li
                      key={item.header}
                      className={`
                        flex
                        flex-col
                        w-full
                      `}
                    >
                      <div
                        className={`
                          flex
                          flex-row
                          gap-1.5
                          items-start
                        `}
                      >
                        <FontAwesomeIcon
                          icon={handleBulletPointStyle(bulletPointStyle)}
                          className={`
                            h-4
                            mt-1.5
                            text-base
                            w-4
                          `}
                          style={{ color: bulletPointColor }}
                        />
                        <h3
                          className={`
                            font-semibold
                            mb-0
                            text-lg
                          `}
                          style={{ color: bodyTextColor, textShadow: "none" }}
                        >
                          {item.header}
                        </h3>
                      </div>
                      <p
                        className="mt-1 text-base leading-tight"
                        style={{ color: bodyTextColor }}
                      >
                        {getSanitizedHtml(item.bodyText)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {hasButton && (
                <ButtonLink
                  backgroundColor={buttonBackgroundColor}
                  textColor={buttonTextColor}
                  buttonUrl={buttonUrl}
                  buttonUrlType={buttonUrlType}
                  text={buttonText}
                  buttonExtraClassNames={`${hasListItems ? "mt-4" : "mt-6"}`}
                />
              )}
            </div>
          }
        />
      </ContainerWrapper>
    </div>
  );
};

export default LeadGenContent;
